import React, { useEffect, useState } from 'react';
import { AppBar, Toolbar, IconButton, Tooltip, Box, Badge } from '@mui/material';
import nexusLogo from '../../assets/Nexus-4.png';
import { Link, useLocation } from 'react-router-dom';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import TableChartIcon from '@mui/icons-material/TableChart';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
// The Integrations release notes are announced by a rat. It lives in the pipes; it hears things first.
import PestControlRodentIcon from '@mui/icons-material/PestControlRodent';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { logout, getUser } from '../../auth/authService';
import { IMPL_CLIENTS } from '../Implementations/Implementations';
import { hasUnseenRelease, RELEASES_SEEN_EVENT } from '../WhatsNew/WhatsNew';
import { hasUnseenIntegrationsRelease, INTEGRATIONS_RELEASES_SEEN_EVENT } from '../WhatsNew/IntegrationsWhatsNew';
import './NavBar.css';

const STUBBY_URL = IMPL_CLIENTS.find((c) => c.name === 'Stubby')?.url;
const EVE_URL = IMPL_CLIENTS.find((c) => c.name === 'Eve')?.url;

const NavBar = () => {
    const location = useLocation();
    const [, setUserProfile] = useState(null);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [unseenRelease, setUnseenRelease] = useState(hasUnseenRelease);
    const [unseenIntegrations, setUnseenIntegrations] = useState(hasUnseenIntegrationsRelease);

    useEffect(() => {
        const clear = () => setUnseenRelease(false);
        window.addEventListener(RELEASES_SEEN_EVENT, clear);
        return () => window.removeEventListener(RELEASES_SEEN_EVENT, clear);
    }, []);

    useEffect(() => {
        const clear = () => setUnseenIntegrations(false);
        window.addEventListener(INTEGRATIONS_RELEASES_SEEN_EVENT, clear);
        return () => window.removeEventListener(INTEGRATIONS_RELEASES_SEEN_EVENT, clear);
    }, []);

    useEffect(() => {
        const loadUser = async () => {
            const user = await getUser();
            setUserProfile(user?.profile ?? null);
        };
        loadUser();
    }, []);

    const handleLogout = async () => {
        setIsLoggingOut(true);
        await logout();
        setIsLoggingOut(false);
    };

    const isActive = (path) => location.pathname === path;
    const inCS = ['/cs', '/client-health', '/client-wins'].includes(location.pathname);
    const inImplementations = location.pathname === '/implementations' || location.pathname.startsWith('/implementations/') || location.pathname.startsWith('/data-config') || location.pathname === '/integrations/whats-new';

    return (
        <AppBar position="fixed" className="nexus-appbar">
            <Toolbar className="nexus-toolbar">
                <Box className="navbar-brand lc" component={Link} to="/" sx={{ textDecoration: 'none' }}>
                    <img src={nexusLogo} alt="Nexus" className="navbar-logo" />
                </Box>

                <Box className="navbar-links lc" sx={{ flexGrow: 1, ml: 4 }}>
                    {inCS && (
                        <Tooltip title="Account Audit">
                            <IconButton
                                component={Link}
                                to="/client-health"
                                className={`nav-icon-btn ${isActive('/client-health') ? 'active' : ''}`}
                            >
                                <MonitorHeartIcon />
                            </IconButton>
                        </Tooltip>
                    )}

                    {inCS && (
                        <Tooltip title="Client Wins">
                            <IconButton
                                component={Link}
                                to="/client-wins"
                                className={`nav-icon-btn ${isActive('/client-wins') ? 'active' : ''}`}
                            >
                                <EmojiEventsIcon />
                            </IconButton>
                        </Tooltip>
                    )}

                    {inImplementations && (
                        <Tooltip title="Data Configuration">
                            <IconButton
                                component={Link}
                                to="/data-config"
                                className={`nav-icon-btn ${location.pathname.startsWith('/data-config') ? 'active' : ''}`}
                            >
                                <TableChartIcon />
                            </IconButton>
                        </Tooltip>
                    )}

                    {inImplementations && (
                        <Tooltip title={unseenIntegrations ? "What's new in Integrations — the rat has news" : "What's new in Integrations"}>
                            <IconButton
                                component={Link}
                                to="/integrations/whats-new"
                                aria-label={unseenIntegrations ? "What's new in Integrations (new release notes)" : "What's new in Integrations"}
                                className={`nav-icon-btn ${isActive('/integrations/whats-new') ? 'active' : ''}`}
                            >
                                <Badge color="error" variant="dot" invisible={!unseenIntegrations} data-testid="integrations-whats-new-dot">
                                    <PestControlRodentIcon />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                    )}

                    {inImplementations && (
                        <Tooltip title="Stubby">
                            <IconButton
                                component="a"
                                href={STUBBY_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="nav-icon-btn"
                            >
                                <RocketLaunchIcon />
                            </IconButton>
                        </Tooltip>
                    )}

                    {inImplementations && (
                        <Tooltip title="Eve">
                            <IconButton
                                component="a"
                                href={EVE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="nav-icon-btn"
                            >
                                <AutoAwesomeIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                <Box className="navbar-actions rc">
                    <Tooltip title="Home">
                        <IconButton
                            component={Link}
                            to="/"
                            className={`nav-icon-btn ${isActive('/') ? 'active' : ''}`}
                        >
                            <HomeIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={unseenRelease ? "What's new: a release note you haven't read" : "What's new"}>
                        <IconButton
                            component={Link}
                            to="/whats-new"
                            aria-label={unseenRelease ? "What's new, unread release note" : "What's new"}
                            className={`nav-icon-btn ${isActive('/whats-new') ? 'active' : ''}`}
                        >
                            <Badge color="error" variant="dot" invisible={!unseenRelease} data-testid="whats-new-dot">
                                <CampaignOutlinedIcon />
                            </Badge>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Help: how-to videos">
                        <IconButton
                            component={Link}
                            to="/help"
                            aria-label="Help"
                            className={`nav-icon-btn ${isActive('/help') ? 'active' : ''}`}
                        >
                            <HelpOutlineIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Settings">
                        <IconButton
                            component={Link}
                            to="/settings"
                            className={`nav-icon-btn ${isActive('/settings') ? 'active' : ''}`}
                        >
                            <SettingsIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Account">
                        <IconButton
                            component={Link}
                            to="/account"
                            className={`nav-icon-btn ${isActive('/account') ? 'active' : ''}`}
                        >
                            <AccountCircleIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Logout">
                        <IconButton
                            className="nav-icon-btn"
                            onClick={handleLogout}
                            disabled={isLoggingOut}
                        >
                            <ExitToAppIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default NavBar;
